import { useState, useCallback } from 'react';
import api from '../utils/api';
import { isPro } from '../Helpers';

export const useBatchProcessor = () => {
    const [batchProgress, setBatchProgress] = useState({
        isProcessing: false,
        isCompleted: false,
        currentBatch: 0,
        totalBatches: 0,
        processedItems: 0,
        totalItems: 0,
        error: null
    });

    const resetProgress = useCallback(() => {
        setBatchProgress({
            isProcessing: false,
            isCompleted: false,
            currentBatch: 0,
            totalBatches: 0,
            processedItems: 0,
            totalItems: 0,
            error: null
        });
    }, []);

    const processBatch = useCallback(async (
        items, 
        operation, 
        data, 
        batchSize = 5000,
        operationType = 'orders'
    ) => {
        console.log('🚀 Starting batch processing:', {
            itemCount: items.length,
            operation: `"${operation}"`,
            operationType: `"${operationType}"`,
            data,
            batchSize
        });

        // Check if user has pro access for batch processing
        if (!isPro()) {
            throw new Error('Batch processing is a premium feature. Please upgrade to Pro.');
        }

        const totalItems = items.length;
        const totalBatches = Math.ceil(totalItems / batchSize);

        console.log(`📊 Batch processing setup: ${totalItems} items in ${totalBatches} batches`);

        setBatchProgress({
            isProcessing: true,
            isCompleted: false,
            currentBatch: 0,
            totalBatches,
            processedItems: 0,
            totalItems,
            error: null
        });

        try {
            let processedItems = 0;

            for (let i = 0; i < totalBatches; i++) {
                const startIndex = i * batchSize;
                const endIndex = Math.min(startIndex + batchSize, totalItems);
                const batchItems = items.slice(startIndex, endIndex);

                console.log(`🔄 Processing batch ${i + 1}/${totalBatches}: items ${startIndex}-${endIndex-1} (${batchItems.length} items)`);

                setBatchProgress(prev => ({
                    ...prev,
                    currentBatch: i + 1
                }));

                // Call the appropriate batch API method
                let response;
                console.log(`🔍 Processing operation: "${operation}" for ${operationType}`);
                
                switch (operation) {
                    case 'updateStatus':
                    case 'statusUpdate': // Alternative name for status updates
                        console.log(`📡 Calling batch${operationType === 'orders' ? 'UpdateOrderStatus' : 'UpdateProductStatus'}`);
                        if (operationType === 'orders') {
                            // For order status updates, we need to send the batch index
                            response = await api.post('/orders/batch-status-update', {
                                order_ids: items, // Send all items, backend will slice based on batch_index
                                status: data.status,
                                batch_size: batchSize,
                                batch_index: i
                            });
                        } else {
                            // For product status updates, send batch index
                            response = await api.post('/products/batch-status-update', {
                                product_ids: items, // Send all items, backend will slice based on batch_index
                                status: data.status,
                                batch_size: batchSize,
                                batch_index: i
                            });
                        }
                        break;
                    case 'updateStock':
                        console.log(`📡 Calling batchUpdateProductStock (legacy)`);
                        // For stock updates, send batch index
                        response = await api.post('/products/batch-stock-update', {
                            stock_updates: data.stockUpdates || data.stockData || [],
                            batch_size: batchSize,
                            batch_index: i
                        });
                        break;
                    case 'stockUpdate':
                        console.log(`📡 Calling batchUpdateProductStock`);
                        // For stock updates, send batch index
                        response = await api.post('/products/batch-stock-update', {
                            stock_updates: data.stockData, // Send all stock updates, backend will slice based on batch_index
                            batch_size: batchSize,
                            batch_index: i
                        });
                        break;
                    case 'delete':
                        console.log(`📡 Calling batch${operationType === 'orders' ? 'DeleteOrders' : 'DeleteProducts'}`);
                        if (operationType === 'orders') {
                            // For order deletion, send batch index
                            response = await api.post('/orders/batch-delete', {
                                order_ids: items, // Send all items, backend will slice based on batch_index
                                force: data.force || false,
                                batch_size: batchSize,
                                batch_index: i
                            });
                        } else {
                            response = await api.post('/products/batch-delete', {
                                product_ids: items, // Send all items, backend will slice based on batch_index
                                force: data.force || false,
                                batch_size: batchSize,
                                batch_index: i
                            });
                        }
                        break;
                    case 'bulkUpdate':
                        if (operationType === 'orders') {
                            // For bulk update, we need to process each item individually in the batch
                            // The data contains { updates: [...] } where each update has { id, data }
                            const updates = data.updates || [];
                            const batchUpdates = updates.slice(startIndex, endIndex);
                            
                            console.log(`📦 Processing ${batchUpdates.length} orders in batch ${i + 1}`);
                            
                            // Process each order in this batch
                            const batchPromises = batchUpdates.map(update => 
                                api.updateOrder(update.id, update.data)
                            );
                            const batchResults = await Promise.all(batchPromises);
                            
                            // Check if all succeeded
                            const successCount = batchResults.filter(r => r.success).length;
                            if (successCount !== batchResults.length) {
                                throw new Error(`Only ${successCount} of ${batchResults.length} orders updated successfully`);
                            }
                            
                            response = { success: true, message: `Batch ${i + 1} completed` };
                        } else {
                            // Similar logic for products
                            const updates = data.updates || [];
                            const batchUpdates = updates.slice(startIndex, endIndex);
                            
                            console.log(`📦 Processing ${batchUpdates.length} products in batch ${i + 1}`);
                            
                            const batchPromises = batchUpdates.map(update => 
                                api.updateProduct(update.id, update.data)
                            );
                            const batchResults = await Promise.all(batchPromises);
                            
                            const successCount = batchResults.filter(r => r.success).length;
                            if (successCount !== batchResults.length) {
                                throw new Error(`Only ${successCount} of ${batchResults.length} products updated successfully`);
                            }
                            
                            response = { success: true, message: `Batch ${i + 1} completed` };
                        }
                        break;
                    default:
                        throw new Error(`Unknown operation: ${operation}`);
                }

                if (!response.success) {
                    console.error(`❌ Batch ${i + 1} failed:`, response);
                    throw new Error(response.message || 'Batch operation failed');
                }

                console.log(`✅ Batch ${i + 1} completed successfully:`, response);

                processedItems += batchItems.length;
                setBatchProgress(prev => ({
                    ...prev,
                    processedItems
                }));

                console.log(`📊 Progress: ${processedItems}/${totalItems} items processed`);

                // Small delay between batches to prevent overwhelming the server
                if (i < totalBatches - 1) {
                    await new Promise(resolve => setTimeout(resolve, 500)); // Increased delay for visibility
                }
            }

            setBatchProgress(prev => ({
                ...prev,
                isProcessing: false,
                isCompleted: true
            }));

            return {
                success: true,
                message: `Successfully processed ${totalItems} items in ${totalBatches} batches`,
                processedItems: totalItems
            };

        } catch (error) {
            setBatchProgress(prev => ({
                ...prev,
                isProcessing: false,
                error: error.message
            }));
            throw error;
        }
    }, []);

    const shouldUseBatchProcessing = useCallback((itemCount, settings) => {
        // Use batch processing if:
        // 1. User has pro access
        // 2. Optimize mode is enabled in settings
        // 3. Item count is above the configured threshold
        const optimizeMode = settings?.bulk_edit_optimize_mode === true || settings?.bulk_edit_optimize_mode === "1" || settings?.bulk_edit_optimize_mode === 1;
        const threshold = parseInt(settings?.bulk_edit_threshold) || 1000;
        
        console.log('🔍 Batch Processing Check:', {
            isPro: isPro(),
            optimizeMode,
            itemCount,
            threshold,
            settings: settings,
            shouldUseBatch: isPro() && optimizeMode && itemCount > threshold
        });
        
        return isPro() && 
               optimizeMode && 
               itemCount > threshold;
    }, []);

    return {
        batchProgress,
        processBatch,
        resetProgress,
        shouldUseBatchProcessing
    };
};

export default useBatchProcessor;